import { getFreshToken } from '../auth/keycloak'
import { getScope, type Scope } from '../auth/scope'
import { runtimeConfig } from '../runtimeConfig'
import type { ProblemDetails } from './types'

/*
  dataroom's fetch wrapper, with two additions for a multi-tenant PWA:

  - X-Organization-Id / X-Ranch-Id from the active scope (or an explicit one, which is
    how the sync engine posts a batch captured under a ranch the user has since left).
  - Offline-aware errors: a fetch that fails because there is no network is a
    NetworkError, not an ApiError, so the sync engine can tell "try later" from "the
    server refused".
*/

export const CORRELATION_HEADER = 'X-Correlation-Id'
export const ORGANIZATION_HEADER = 'X-Organization-Id'
export const RANCH_HEADER = 'X-Ranch-Id'

/**
 * Resolves an API path against the configured origin. Read per call from the runtime
 * configuration rather than captured at module load, because it arrives from
 * /config.json at startup.
 */
export function apiUrl(path: string): string {
  const base = runtimeConfig().apiBaseUrl
  return base ? `${base}${path}` : path
}

/** An API failure carrying the server's Problem Details and the request's correlation id. */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  readonly correlationId: string | null

  constructor(status: number, problem: ProblemDetails, correlationId: string | null) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.correlationId = correlationId
  }

  /** Field-level violations, keyed by camelCase field name, so a form can show them inline. */
  get violations(): Record<string, string> {
    const map: Record<string, string> = {}
    for (const v of this.problem.violations ?? []) {
      map[v.field] = v.message
    }
    return map
  }
}

/** The request never reached the server: offline, DNS, CORS preflight refused. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The server could not be reached.')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

/** A successful response together with the correlation id the API stamped on it. */
export interface ApiResponse<T> {
  data: T
  correlationId: string | null
}

export interface RequestOptions {
  /** Overrides the active scope; `null` sends no scope headers (platform routes). */
  scope?: Scope | null
  /** Extra headers, e.g. an organization-only scope for a route that lists its ranches. */
  headers?: Record<string, string>
  signal?: AbortSignal
}

/**
 * A W3C `traceparent` for one request: `00-<trace-id>-<parent-id>-01`.
 *
 * ASP.NET Core reads it into Activity.Current, and the API's correlation id IS the
 * trace id -- so the id the browser generated is the one on the log line, the Problem
 * Details body and every audit row. Random per request; there is no browser-side span.
 */
export function newTraceparent(random: (bytes: Uint8Array) => Uint8Array = fill): string {
  const traceId = hex(random(new Uint8Array(16)))
  const parentId = hex(random(new Uint8Array(8)))
  return `00-${traceId}-${parentId}-01`
}

function fill(bytes: Uint8Array): Uint8Array {
  crypto.getRandomValues(bytes)
  return bytes
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The scope headers for a request: the explicit scope, else the active one, else none. */
export function scopeHeaders(scope: Scope | null | undefined): Record<string, string> {
  const effective = scope === undefined ? getScope() : scope
  if (!effective) return {}
  return { [ORGANIZATION_HEADER]: effective.organizationId, [RANCH_HEADER]: effective.ranchId }
}

async function request<T>(
  path: string,
  init?: RequestInit,
  options?: RequestOptions,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    traceparent: newTraceparent(),
    ...(init?.body === undefined || init.body instanceof Blob || init.body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json' }),
    ...scopeHeaders(options?.scope),
    ...options?.headers,
    ...(init?.headers as Record<string, string> | undefined),
  }

  const token = await getFreshToken(30)
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  let response: Response
  try {
    response = await fetch(apiUrl(path), { ...init, headers, signal: options?.signal })
  } catch (error) {
    throw new NetworkError(error)
  }
  const correlationId = response.headers.get(CORRELATION_HEADER)

  if (response.status === 204) {
    return { data: undefined as T, correlationId }
  }

  const text = await response.text()
  // A CSV export (finance entries, reports) is the body itself, not JSON.
  if (response.ok && response.headers.get('Content-Type')?.startsWith('text/csv')) {
    return { data: text as T, correlationId }
  }
  let payload: unknown = undefined
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { title: text }
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, (payload ?? {}) as ProblemDetails, correlationId)
  }

  return { data: payload as T, correlationId }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, undefined, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(
      path,
      { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) },
      options,
    ),

  put: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, options),

  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, options),

  del: (path: string, options?: RequestOptions) =>
    request<void>(path, { method: 'DELETE' }, options),

  /** Raw bytes (an imagery chunk). JSON-encoding a Blob would send "{}". */
  putBytes: (path: string, bytes: Blob, options?: RequestOptions) =>
    request<void>(
      path,
      { method: 'PUT', body: bytes, headers: { 'Content-Type': 'application/octet-stream' } },
      options,
    ),

  /** A file as bytes (a note's audio or photo): the media routes need the bearer token, so an
   * <audio> or <img> cannot fetch them directly; the caller makes an object URL. */
  getBlob: async (path: string, options?: RequestOptions): Promise<Blob> => {
    const headers: Record<string, string> = {
      traceparent: newTraceparent(),
      ...scopeHeaders(options?.scope),
      ...options?.headers,
    }
    const token = await getFreshToken(30)
    if (token) headers.Authorization = `Bearer ${token}`
    let response: Response
    try {
      response = await fetch(apiUrl(path), { headers, signal: options?.signal })
    } catch (error) {
      throw new NetworkError(error)
    }
    if (!response.ok) {
      let problem: ProblemDetails = { title: response.statusText }
      try {
        problem = (await response.json()) as ProblemDetails
      } catch {
        // not JSON
      }
      throw new ApiError(response.status, problem, response.headers.get(CORRELATION_HEADER))
    }
    return response.blob()
  },

  /** Multipart upload (the theme logo). The browser sets the boundary; no Content-Type here. */
  uploadForm: <T>(path: string, form: FormData, options?: RequestOptions) =>
    request<T>(path, { method: 'POST', body: form }, options),
}
